import { useCallback, useEffect, useState } from 'react';
import type { PermissionLevel, SharingGrant } from '@reef/schema';
import { api, type GroupSummary, type WorkflowSharing } from '../api.ts';

/**
 * Who else can reach this workflow.
 *
 * Workflows are private by default, which is the right default and was, until
 * now, close to a dead end: `PUT /workflows/:id/sharing` has always existed and
 * nothing in the studio called it, so the only way to let a colleague see your
 * workflow was an API request. The observable result was people sharing a
 * *link* to a run instead — a bearer token that bypasses the session — because
 * that was the only sharing the UI offered.
 *
 * Three things this screen has to get right, and each is a decision rather than
 * a detail.
 *
 * **A group grant says how many people it reaches.** "Shared with eng-all" is a
 * different statement when eng-all is four people and when it is four hundred,
 * and the number is what makes somebody think twice before picking `admin`.
 *
 * **A person is named by an address you already have**, never chosen from a
 * list — reef has no directory search, deliberately, so that a signed-in
 * account is not a way to dump the staff list.
 *
 * **Changing sharing needs `admin`, not `edit`.** Somebody who can edit the
 * document cannot widen its audience, so the whole editor renders read-only
 * for them rather than offering controls the API will refuse.
 */
export function SharingEditor({ workflowId }: { workflowId: string }) {
  const [sharing, setSharing] = useState<WorkflowSharing | null>(null);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [kind, setKind] = useState<'user' | 'group'>('user');
  const [email, setEmail] = useState('');
  const [groupId, setGroupId] = useState('');
  const [level, setLevel] = useState<PermissionLevel>('read');

  const refresh = useCallback(async () => {
    try {
      setSharing(await api.workflowSharing(workflowId));
      setError(null);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }, [workflowId]);

  useEffect(() => {
    void refresh();
    void api
      .listGroups()
      .then((result) => setGroups(result.groups))
      .catch(() => setGroups([]));
  }, [refresh]);

  if (!sharing) return null;

  const save = async (grants: SharingGrant[], said: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.updateSharing(workflowId, grants);
      setNote(said);
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const grantsNow = (): SharingGrant[] =>
    sharing.grants.map((grant) => ({
      principal: grant.principal,
      id: grant.id,
      level: grant.level,
      ...(grant.grantedBy ? { grantedBy: grant.grantedBy } : {}),
      ...(grant.grantedAt ? { grantedAt: grant.grantedAt } : {}),
    }));

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      let id = groupId;
      if (kind === 'user') {
        // Resolved before the grant is written, so an address reef has never
        // seen fails here with something actionable rather than becoming a
        // grant that silently matches nobody.
        id = (await api.resolveUser(email.trim())).userId;
      }
      if (!id) return;

      const existing = grantsNow().filter(
        (grant) => !(grant.principal === kind && grant.id === id),
      );
      await save(
        [...existing, { principal: kind, id, level }],
        kind === 'user' ? `${email.trim()} can now ${level} this.` : `That group can now ${level} this.`,
      );
      setEmail('');
      setGroupId('');
    } catch (caught) {
      setError((caught as Error).message);
      setBusy(false);
    }
  };

  const remove = (grant: WorkflowSharing['grants'][number]) =>
    save(
      grantsNow().filter((entry) => !(entry.principal === grant.principal && entry.id === grant.id)),
      `${grant.label} can no longer reach this.`,
    );

  const editable = sharing.canEditSharing;

  return (
    <div className="card">
      <h2>Who can reach this</h2>

      {error && <div className="banner danger">{error}</div>}
      {note && !error && <div className="banner">{note}</div>}

      <table>
        <thead>
          <tr>
            <th>Who</th>
            <th>Reaches</th>
            <th>Level</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{sharing.owner.label}</td>
            <td className="hint">1</td>
            <td>
              {/*
                The owner always holds `admin`, independent of the grant list,
                so it is shown as a fact rather than as an editable row — a
                removable owner would be a workflow nobody can administer.
              */}
              <span className="pill accent">owner</span>
            </td>
            <td />
          </tr>

          {sharing.grants.map((grant) => (
            <tr key={`${grant.principal}:${grant.id}`}>
              <td>
                {grant.label}
                {grant.principal === 'group' && <span className="pill">group</span>}
              </td>
              <td className="hint">
                {grant.principal === 'group'
                  ? `${grant.memberCount} ${grant.memberCount === 1 ? 'person' : 'people'}`
                  : '1'}
              </td>
              <td>
                <span className={grant.level === 'read' ? 'pill' : 'pill accent'}>{grant.level}</span>
              </td>
              <td>
                {editable && (
                  <button className="ghost" onClick={() => void remove(grant)} disabled={busy}>
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sharing.grants.length === 0 && (
        <p className="hint">
          Private. Nobody but the owner can open it, start it, or see its runs — including in
          Slack, where the same check runs on every message.
        </p>
      )}

      {!editable ? (
        <p className="hint">
          Changing who can reach a workflow needs <code>admin</code> on it. Editing what it does
          needs only <code>edit</code>, which is why you can see this and not change it.
        </p>
      ) : (
        <>
          <div className="studio-row" style={{ marginTop: '0.75rem' }}>
            <div className="field">
              <label htmlFor="share-kind">Share with</label>
              <select
                id="share-kind"
                value={kind}
                onChange={(event) => setKind(event.target.value as 'user' | 'group')}
              >
                <option value="user">a person</option>
                <option value="group">a group</option>
              </select>
            </div>

            {kind === 'user' ? (
              <div className="field">
                <label htmlFor="share-email">Their email</label>
                <input
                  id="share-email"
                  type="email"
                  placeholder="dana.oyelowo@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
            ) : (
              <div className="field">
                <label htmlFor="share-group">Group</label>
                <select
                  id="share-group"
                  value={groupId}
                  onChange={(event) => setGroupId(event.target.value)}
                >
                  <option value="">choose a group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.displayName} ({group.memberCount})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="field">
              <label htmlFor="share-level">Level</label>
              <select
                id="share-level"
                value={level}
                onChange={(event) => setLevel(event.target.value as PermissionLevel)}
              >
                <option value="read">read — see it and its runs</option>
                <option value="edit">edit — change it, start it, decide its reviews</option>
                <option value="admin">admin — and change who else can reach it</option>
              </select>
            </div>

            <button
              onClick={() => void add()}
              disabled={busy || (kind === 'user' ? email.trim().length === 0 : groupId === '')}
            >
              {busy ? 'Saving…' : 'Share'}
            </button>
          </div>

          {/*
            Said once, next to the control that does it. `edit` is the level that
            decides approvals and stops runs, which is a bigger grant than the
            word suggests — somebody reading "edit" thinks "can change the
            document" and not "can approve a production deploy".
          */}
          <p className="hint">
            <code>edit</code> also decides this workflow's reviews and can stop its runs. Every
            sharing change is audited with the person who made it, like every other change to the
            document.
          </p>
        </>
      )}
    </div>
  );
}
